/**
 * Évolution des prix au m² — série trimestrielle sur 10 ans autour de la
 * parcelle (rayon 500 m, fallback 1 km).
 *
 * Source : CEREMA DVF+ (open data DGFiP). Agrégation + bucketing faits
 * côté Cloud Function `getPriceHistory` — le client reçoit la série prête
 * à tracer. La CF cache au niveau commune+grid (100 m), ce hook cache en
 * plus au niveau fiche pour éviter la round-trip callable à chaque ouverture.
 *
 * TTL client 30 jours — aligné sur la cadence DVF (CEREMA publie 2x/an).
 */

import { useMemo } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { useEnvBlock } from "./shared";
import type { GeocodeResult, PriceHistory } from "../types";

const TTL_DAYS = 30;

type Input = {
  lat: number;
  lng: number;
  insee: string;
  communeName: string;
};

const callPriceHistory = httpsCallable<Input, PriceHistory>(
  functions,
  "getPriceHistory",
);

export async function fetchPriceHistory(input: Input): Promise<PriceHistory> {
  const res = await callPriceHistory(input);
  return res.data;
}

export function usePriceHistory(
  ficheId: string | null,
  geocode: GeocodeResult | null,
) {
  const opts = useMemo(() => {
    if (!ficheId || !geocode) return null;
    // Same 3-decimal snap as the backend grid so a tiny BAN jitter doesn't
    // bust the per-fiche cache. The server applies its own snap too — they
    // align.
    const latKey = geocode.lat.toFixed(3);
    const lngKey = geocode.lng.toFixed(3);
    return {
      ficheId,
      blockId: "priceHistory" as const,
      // v4 : invalide les caches client qui ont stocké `communeSeries: null`
      // pendant la fenêtre où le fetch commune CEREMA échouait systématiquement
      // (ordering=-datemut ignoré → 15 s/page → timeout). Depuis le sharding
      // par anneemut en parallèle, le fetch commune passe en ~1-3 s.
      inputsKey: `v4|${geocode.insee}|${latKey}|${lngKey}`,
      ttlDays: TTL_DAYS,
      fetcher: () =>
        fetchPriceHistory({
          lat: geocode.lat,
          lng: geocode.lng,
          insee: geocode.insee,
          communeName: geocode.city,
        }),
    };
  }, [ficheId, geocode]);

  return useEnvBlock<PriceHistory>(opts);
}
